import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../theme/untitled/Modal/Modal";
import Button from "../../theme/untitled/Button/Button";
import ChipsInput from "../../theme/untitled/ChipsInput/ChipsInput";
import Select from "../../theme/untitled/Select/Select";
import Icon from "../../theme/untitled/icons/Icon";
import { isValidEmail } from "../../../../helpers/calendar";
import { ROLE_ADMIN, ROLE_LABELS, ROLE_MEMBER, ROLE_OWNER } from "./constants";
import styles from "./MembersPage.module.scss";

const ROLE_OPTIONS = [ROLE_MEMBER, ROLE_ADMIN, ROLE_OWNER].map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

const InviteDialog = ({ open, onClose, onSubmit, seatsFree, sending }) => {
  const [emails, setEmails] = useState([]);
  const [role, setRole] = useState(ROLE_MEMBER);

  useEffect(() => {
    if (!open) {
      setEmails([]);
      setRole(ROLE_MEMBER);
    }
  }, [open]);

  const onEmailsChange = useCallback((next) => setEmails(next), []);
  const onRoleChange = useCallback((e) => setRole(e.target.value), []);

  const exceedsSeats = seatsFree != null && emails.length > seatsFree;
  const blockedEmails = exceedsSeats ? emails.slice(seatsFree) : [];

  const onSend = useCallback(
    async (e) => {
      e.preventDefault();
      if (exceedsSeats || emails.length === 0 || sending) return;
      await onSubmit(emails, role);
      onClose();
    },
    [exceedsSeats, emails, sending, onSubmit, role, onClose]
  );

  return (
    <Modal
      open={open}
      onClose={sending ? undefined : onClose}
      size="lg"
      icon={<Icon name="UserPlus01" size={24} />}
      title="Invite members"
      footer={
        <>
          <Button hierarchy="secondaryGray" onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button hierarchy="primary" type="submit" form="invite-members-form" disabled={exceedsSeats || emails.length === 0} loading={sending}>
            {sending ? "Sending…" : "Send invitations"}
          </Button>
        </>
      }
    >
      <p>They get an email invitation.</p>
      <form id="invite-members-form" onSubmit={onSend}>
        <div className={styles.dialogField}>
          <ChipsInput
            label="Email addresses"
            value={emails}
            onChange={onEmailsChange}
            placeholder="name@company.com"
            validate={isValidEmail}
            disabled={sending}
          />
          <p className={styles.dialogHint}>Separate addresses with a comma or press Enter.</p>
        </div>

        <div className={styles.dialogField}>
          <label htmlFor="invite-role">Role</label>
          <Select id="invite-role" value={role} onChange={onRoleChange} options={ROLE_OPTIONS} disabled={sending} />
          <p className={styles.dialogHint}>
            Members can view and edit content. Admins can also manage billing.
          </p>
        </div>

        {exceedsSeats ? (
          <div className={styles.dialogError}>
            <Icon name="AlertCircle" size={20} className={styles.dialogErrorIcon} />
            <div className={styles.dialogErrorText}>
              <strong>Not enough seats for every invitation.</strong>
              {seatsFree === 0
                ? " No seats are free right now."
                : ` ${seatsFree} seat${seatsFree === 1 ? " is" : "s are"} free, so ${blockedEmails.join(", ")} can't be invited yet.`}{" "}
              Add a seat, or remove {blockedEmails.length === 1 ? "that address" : "those addresses"} and invite them later.
            </div>
          </div>
        ) : (
          seatsFree != null && (
            <p className={styles.dialogNote}>
              <Icon name="AlertCircle" size={16} className={styles.dialogNoteIcon} />
              <span>
                {seatsFree} of your seats {seatsFree === 1 ? "is" : "are"} free. Each accepted
                invitation takes one.
              </span>
            </p>
          )
        )}
      </form>
    </Modal>
  );
};

export default InviteDialog;
